import React, { useEffect, useMemo, useState } from 'react';
import { AlertCircle, Plus, RefreshCw } from 'lucide-react';
import { useTechAuth } from '@/contexts/TechAuthContext';
import { supabase } from '@/lib/supabase';
import {
  createAdminContest,
  listAdminContests,
  TechContest,
  TechContestGoalType,
  updateAdminContest,
} from '@/lib/tech-api-client';

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return String(error || 'Unknown error');
}

const GOAL_TYPE_OPTIONS: Array<{ value: TechContestGoalType; label: string; hint: string }> = [
  { value: 'first_to', label: 'First to goal', hint: 'Winner is the first tech to hit the goal value.' },
  { value: 'most_sales', label: 'Most sales', hint: 'Winner is the tech with the most sales in the date window.' },
  { value: 'highest_rate', label: 'Highest close rate', hint: 'Winner is the tech with the best close rate in the window.' },
];

export default function TechAdminContestsPage() {
  const { accessToken } = useTechAuth();

  const getAccessToken = async (): Promise<string> => {
    if (accessToken) return accessToken;

    const { data, error: sessionErr } = await supabase.auth.getSession();
    if (sessionErr) throw sessionErr;

    const token = data.session?.access_token;
    if (!token) throw new Error('Missing auth token. Please sign in again.');
    return token;
  };

  const [rows, setRows] = useState<TechContest[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [goalType, setGoalType] = useState<TechContestGoalType>('first_to');
  const [goalValue, setGoalValue] = useState('10');
  const [prizeDescription, setPrizeDescription] = useState('');
  const [prizeAmount, setPrizeAmount] = useState('200');
  const [isActive, setIsActive] = useState(true);

  const load = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = await getAccessToken();
      const list = await listAdminContests(token);
      setRows(list);
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  const sortedRows = useMemo(() => {
    return [...rows].sort((a, b) => String(b.start_date || '').localeCompare(String(a.start_date || '')));
  }, [rows]);

  const goalHint = useMemo(() => {
    return GOAL_TYPE_OPTIONS.find((opt) => opt.value === goalType)?.hint || '';
  }, [goalType]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();

    setCreating(true);
    setError(null);

    try {
      const token = await getAccessToken();

      const goalValueNum = goalValue.trim() ? Number(goalValue) : null;
      const prizeAmountNum = prizeAmount.trim() ? Number(prizeAmount) : null;

      const created = await createAdminContest(token, {
        name: name.trim(),
        description: description.trim() ? description.trim() : null,
        start_date: startDate,
        end_date: endDate,
        goal_type: goalType,
        goal_value: goalValueNum === null ? null : goalValueNum,
        prize_description: prizeDescription.trim() ? prizeDescription.trim() : null,
        prize_amount: prizeAmountNum === null ? null : prizeAmountNum,
        is_active: isActive,
      });

      setRows((prev) => [created, ...prev]);
      setShowCreate(false);

      setName('');
      setDescription('');
      setStartDate('');
      setEndDate('');
      setGoalType('first_to');
      setGoalValue('10');
      setPrizeDescription('');
      setPrizeAmount('200');
      setIsActive(true);
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setCreating(false);
    }
  };

  const toggleActive = async (contest: TechContest) => {
    setError(null);

    try {
      const token = await getAccessToken();
      const updated = await updateAdminContest(token, contest.id, { is_active: !contest.is_active });
      setRows((prev) => prev.map((r) => (r.id === contest.id ? updated : r)));
    } catch (e) {
      setError(getErrorMessage(e));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Contests</h1>
          <p className="text-gray-600 mt-1">Create and manage monthly sales competitions for technicians.</p>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={load}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-gray-200 hover:bg-gray-50"
            disabled={loading}
          >
            <RefreshCw className={['w-4 h-4', loading ? 'animate-spin' : ''].join(' ')} />
            Refresh
          </button>

          <button
            type="button"
            onClick={() => setShowCreate((v) => !v)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-700"
          >
            <Plus className="w-4 h-4" />
            New contest
          </button>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-2xl">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <p className="text-sm text-red-800 font-medium">{error}</p>
          </div>
        </div>
      )}

      {showCreate && (
        <div className="bg-white border border-gray-200 rounded-2xl p-5">
          <h2 className="text-lg font-semibold text-gray-900">Create contest</h2>
          <p className="text-sm text-gray-600 mt-1">{goalHint}</p>

          <form onSubmit={handleCreate} className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-xl"
                placeholder='February Challenge: First to 10 wins $200'
                required
                disabled={creating}
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Description (optional)</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-xl"
                rows={3}
                placeholder="Short motivational description shown to techs."
                disabled={creating}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Start date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-xl"
                required
                disabled={creating}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">End date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-xl"
                required
                disabled={creating}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Goal type</label>
              <select
                value={goalType}
                onChange={(e) => setGoalType(e.target.value as TechContestGoalType)}
                className="w-full px-3 py-2 border border-gray-300 rounded-xl"
                disabled={creating}
              >
                {GOAL_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Goal value</label>
              <input
                value={goalValue}
                onChange={(e) => setGoalValue(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-xl"
                placeholder={goalType === 'first_to' ? '10' : 'Optional'}
                disabled={creating}
              />
              <div className="mt-1 text-xs text-gray-500">
                For <span className="font-medium">First to goal</span>, this is required (ex: 10). For <span className="font-medium">Highest close rate</span>, we treat it as a minimum presentation count.
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Prize description (optional)</label>
              <input
                value={prizeDescription}
                onChange={(e) => setPrizeDescription(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-xl"
                placeholder="$200 gift card"
                disabled={creating}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Prize amount (optional)</label>
              <input
                value={prizeAmount}
                onChange={(e) => setPrizeAmount(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-xl"
                placeholder="200"
                disabled={creating}
              />
            </div>

            <div className="md:col-span-2 flex items-center gap-2">
              <input
                id="is_active"
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                disabled={creating}
              />
              <label htmlFor="is_active" className="text-sm text-gray-700">
                Active
              </label>
            </div>

            <div className="md:col-span-2 flex items-center gap-3">
              <button
                type="submit"
                className="px-5 py-2 rounded-xl bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50"
                disabled={creating}
              >
                {creating ? 'Creating…' : 'Create contest'}
              </button>

              <button
                type="button"
                onClick={() => setShowCreate(false)}
                className="px-5 py-2 rounded-xl bg-white border border-gray-300 hover:bg-gray-50"
                disabled={creating}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-2xl p-5">
        <h2 className="text-lg font-semibold text-gray-900">All contests</h2>
        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500">
                <th className="py-2 pr-4">Name</th>
                <th className="py-2 pr-4">Window</th>
                <th className="py-2 pr-4">Goal</th>
                <th className="py-2 pr-4">Prize</th>
                <th className="py-2 pr-4">Active</th>
                <th className="py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-6 text-gray-500">
                    No contests found.
                  </td>
                </tr>
              ) : (
                sortedRows.map((contest) => (
                  <tr key={contest.id} className="border-t border-gray-100">
                    <td className="py-3 pr-4">
                      <div className="font-medium text-gray-900">{contest.name}</div>
                      <div className="text-xs text-gray-500">{contest.goal_type}</div>
                    </td>
                    <td className="py-3 pr-4">
                      {contest.start_date} → {contest.end_date}
                    </td>
                    <td className="py-3 pr-4">{contest.goal_value ?? '—'}</td>
                    <td className="py-3 pr-4">
                      {contest.prize_amount !== null ? `$${contest.prize_amount}` : contest.prize_description || '—'}
                    </td>
                    <td className="py-3 pr-4">
                      <span
                        className={
                          contest.is_active
                            ? 'inline-flex px-2 py-1 rounded-lg bg-green-100 text-green-800 text-xs'
                            : 'inline-flex px-2 py-1 rounded-lg bg-gray-100 text-gray-700 text-xs'
                        }
                      >
                        {contest.is_active ? 'Active' : 'Disabled'}
                      </span>
                    </td>
                    <td className="py-3">
                      <button
                        type="button"
                        onClick={() => toggleActive(contest)}
                        className="px-3 py-1 rounded-lg bg-white border border-gray-300 hover:bg-gray-50"
                      >
                        {contest.is_active ? 'Disable' : 'Enable'}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
